import { useForm } from '@tanstack/react-form'
import * as z from 'zod'
import {
  Alert,
  AlertProps,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'

import axios, { pluckError } from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
  zodValidationSchema,
} from 'src/v3/utilities/validation'
import {
  AdminIcon,
  CancelIcon,
  CreatorIcon,
  EditIcon,
  ManagerIcon,
  SaveIcon,
  StudentIcon,
  SubmitIcon,
} from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles, selectUserID } from 'src/v3/settings/settings.slice'
import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import ImageUpload from 'src/v3/components/upload-image'
import theme from 'src/theme'

import BasePage from './layout'
import {
  fetchLinkedServices,
  fetchUser,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectUser,
  selectUserStatus,
} from '../profile.slice'

type UserFormFields = {
  phone: string
  address: string
  city: string
  state: string
  zip: string
}

type SnackbarAlert = {
  message: string
  type: AlertProps['severity']
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let userId = useAppSelector(selectUserID)
  let roles = useAppSelector(selectRoles)
  let user = useAppSelector(selectUser)
  let userStatus = useAppSelector(selectUserStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let [existingImage, setExistingImage] = useState<string | undefined>(
    undefined,
  )
  let [profileImage, setProfileImage] = useState<File | undefined>(undefined)
  let [editImageOpen, setEditImageOpen] = useState<boolean>(false)
  let [saving, setSaving] = useState<boolean>(false)
  let [uploading, setUploading] = useState<boolean>(false)
  let [alert, setAlert] = useState<SnackbarAlert | null>(null)

  let formValues: UserFormFields = {
    phone: user?.phone ?? '',
    address: user?.address ?? '',
    city: user?.city ?? '',
    state:
      !!user?.state && user.state !== 'Other' && user.state !== ''
        ? user?.state
        : 'unselected',
    zip: user?.zip ?? '',
  }

  let validationSchema = z.object({
    phone: zodValidationSchema.phone.nonempty('Phone is Required'),
    address: z.string().nonempty('Address is Required'),
    city: z.string().nonempty('City is Required'),
    state: z
      .string()
      .nonempty('State is Required')
      .refine((val) => val !== 'unselected', {
        message: 'State is Required',
      }),
    zip: z.string().nonempty('Zip Code is Required'),
  })

  let form = useForm({
    defaultValues: formValues,
    validators: { onChange: validationSchema },
    onSubmit: async ({ value }) => {
      setSaving(true)
      axios
        .put('/api/v2/profile/user', {
          id: user?.id,
          primaryEmail: user?.primaryEmail,
          phone: value.phone,
          address: value.address,
          city: value.city,
          state: value.state,
          zip: value.zip,
        })
        .then(() => dispatch(fetchUser()))
        .then(() => form.reset())
        .then(() => setAlert({ type: 'success', message: 'Saved' }))
        .catch((err) => {
          setAlert({ type: 'error', message: pluckError(err) })
        })
        .finally(() => setSaving(false))
    },
  })

  useEffect(() => {
    if (userStatus === 'uninitialized') {
      dispatch(fetchUser())
    }
  }, [userStatus])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (userId) {
      axios
        .get(`/api/v2/profile/image`)
        .then(({ data }) => data)
        .then(setExistingImage)
        .catch(() => setExistingImage(undefined))
    }
  }, [userId])

  let gap = 2.5
  let flexWrap = { xs: 'wrap', sm: 'nowrap' }

  return (
    <BasePage>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap, flexGrow: 50 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: gap,
                rowGap: gap,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Avatar
                  variant="rounded"
                  src={existingImage}
                  sx={{ height: 188, width: 188 }}
                  alt={`${user?.firstName} ${user?.lastName}'s profile picture`}
                />
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -30,
                      right: -10,
                      bgcolor: theme.palette.primary.dark,
                      borderRadius: '100%',
                      outline: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Tooltip title="Update Profile Picture" placement="right">
                      <IconButton
                        onClick={() => setEditImageOpen(!editImageOpen)}
                        aria-label="Update profile picture"
                        aria-expanded={editImageOpen}
                        aria-controls="profile-image-upload-section"
                      >
                        <EditIcon aria-hidden="true" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              {editImageOpen && (
                <Box
                  sx={{ flexGrow: 1, maxWidth: 600 }}
                  id="profile-image-upload-section"
                >
                  <ImageUpload
                    id="profile-image"
                    type="default"
                    image={profileImage}
                    setImage={setProfileImage}
                    imageBasePath="public"
                  />
                  {profileImage && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        color="primary"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        loading={uploading}
                        onClick={() => {
                          setUploading(true)
                          axios
                            .put('/api/v2/profile/image', {
                              userId,
                              imageType:
                                profileImage?.type === 'image/png'
                                  ? 'png'
                                  : 'jpg',
                            })
                            .then((response) =>
                              axios.put(response.data, profileImage, {
                                headers: {
                                  'Cache-Control': 'max-age=2592000',
                                  'content-type': profileImage?.type,
                                },
                              }),
                            )
                            .then(() =>
                              axios
                                .get(`/api/v2/profile/image`)
                                .then(({ data }) => data)
                                .then(setExistingImage)
                                .catch(() => setExistingImage(undefined)),
                            )
                            .then(() => {
                              setAlert({ type: 'success', message: 'Saved' })
                              setProfileImage(undefined)
                            })
                            .catch((err) => {
                              setAlert({
                                type: 'error',
                                message: pluckError(err),
                              })
                            })
                            .finally(() => setUploading(false))
                        }}
                      >
                        Save Profile Picture
                      </Button>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => setProfileImage(undefined)}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  alignSelf: 'flex-end',
                  flexBasis: editImageOpen ? '100%' : undefined,
                }}
              >
                <Box
                  sx={{
                    mb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ pb: 0.5, flexGrow: 1 }}
                    id="account-details-heading"
                  >
                    Account Details
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap, gap, mb: gap }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.paperDark' }}
                    value={user?.firstName || ''}
                    label="First Name"
                    name="firstName"
                    disabled
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.paperDark' }}
                    value={user?.lastName || ''}
                    label="Last Name"
                    name="lastName"
                    disabled
                  />
                </Box>
                <Box sx={{ display: 'flex', flexWrap, gap }}>
                  <form.Field
                    name="phone"
                    children={(field) => (
                      <TextField
                        fullWidth
                        variant="outlined"
                        sx={{ bgcolor: 'background.paperDark' }}
                        value={field.state.value}
                        label="Phone"
                        name={field.name}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        error={shouldShowZodFieldError(field.state.meta)}
                        helperText={
                          shouldShowZodFieldError(field.state.meta) &&
                          getZodValidationError(field.state.meta.errors)
                        }
                        required
                        aria-invalid={shouldShowZodFieldError(field.state.meta)}
                        slotProps={{
                          htmlInput: {
                            'aria-label': 'Phone number',
                          },
                        }}
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.paperDark' }}
                    value={user?.primaryEmail || ''}
                    label="Email"
                    name="email"
                    disabled
                  />
                </Box>
              </Box>
            </Box>
            <form.Field
              name="address"
              children={(field) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: 'background.paperDark' }}
                  value={field.state.value}
                  label="Address"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta) &&
                    getZodValidationError(field.state.meta.errors)
                  }
                  required
                  aria-invalid={shouldShowZodFieldError(field.state.meta)}
                  slotProps={{
                    htmlInput: {
                      'aria-label': 'Street address',
                    },
                  }}
                />
              )}
            />
            <Box sx={{ display: 'flex', flexWrap, gap }}>
              <form.Field
                name="city"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.paperDark' }}
                    value={field.state.value}
                    label="City"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                    required
                    aria-invalid={shouldShowZodFieldError(field.state.meta)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'City',
                      },
                    }}
                  />
                )}
              />
              <form.Field
                name="state"
                children={(field) => (
                  <StateDropdown
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    variant="outlined"
                  />
                )}
              />
              <form.Field
                name="zip"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.paperDark' }}
                    value={field.state.value}
                    label="Zip Code"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                    required
                    aria-invalid={shouldShowZodFieldError(field.state.meta)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'Zip code',
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <form.Subscribe
                selector={(state) => state.isSubmitting || !state.isValid}
                children={(isDisabled) => (
                  <Button
                    color="primary"
                    type="submit"
                    size="large"
                    variant="contained"
                    startIcon={<SubmitIcon />}
                    disabled={isDisabled}
                    loading={saving}
                  >
                    Save Changes
                  </Button>
                )}
              />
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 300px', maxWidth: 500 }}>
            {linkedServices.length > 0 && (
              <Card variant="outlined" sx={{ p: 2, mb: gap }}>
                <Typography variant="h6" sx={{ mb: 1.25 }}>
                  Linked Services
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {linkedServices.map((ls) => (
                    <Chip
                      color="primary"
                      key={ls.name}
                      label={ls.name}
                      variant="filled"
                      aria-label={`Linked service: ${ls.name}`}
                      avatar={
                        <Avatar
                          src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${ls?.imageName}`}
                          alt={`${ls.name} logo`}
                        >
                          LS
                        </Avatar>
                      }
                    />
                  ))}
                </Box>
              </Card>
            )}
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.25 }}>
                Roles
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  { name: 'Admin', icon: <AdminIcon /> },
                  { name: 'Manager', icon: <ManagerIcon /> },
                  { name: 'Creator', icon: <CreatorIcon /> },
                  { name: 'Student', icon: <StudentIcon /> },
                ]
                  .filter((role) => roles?.includes(role.name.toLowerCase()))
                  .map((role) => (
                    <Chip
                      color="secondary"
                      key={role.name}
                      label={role.name}
                      variant="filled"
                      icon={role.icon}
                      aria-label={`User role: ${role.name}`}
                    />
                  ))}
              </Box>
            </Card>
          </Box>
        </Box>
        <Snackbar
          open={!!alert}
          onClose={() => setAlert(null)}
          autoHideDuration={alert?.type !== 'error' ? 3000 : undefined}
          aria-live="polite"
          aria-atomic="true"
        >
          <Alert
            variant="filled"
            onClose={() => setAlert(null)}
            severity={alert?.type}
            role="alert"
            aria-live={alert?.type === 'error' ? 'assertive' : 'polite'}
          >
            {alert?.message}
          </Alert>
        </Snackbar>
      </form>
    </BasePage>
  )
}
